/**
 * Conversations — UI state slice.
 *
 * Owns:
 *   - Which profile is currently active (URL-driven, mirrored here for selectors).
 *   - Ad-hoc filter overrides layered on top of the active profile's saved
 *     filters. Lets the user tweak filters without immediately mutating the
 *     saved profile (Linear/Front pattern: "modify view → Save changes" CTA).
 *   - Which thread is selected within the active profile (URL-driven, used by
 *     the right pane and the list highlight).
 *   - Sidebar collapsed flag (UI pref).
 *
 * Does NOT own:
 *   - Profile list (React Query — `useListProfiles`).
 *   - Thread list (React Query — Phase 2b `useListThreads`).
 *   - Realtime patches (Phase 3 — gateway middleware will dispatch into this
 *     slice for selection invariants only; thread cache is React Query's job).
 *
 * The "filter overrides" layer is the source of `isFilterDirty`: if any field
 * differs from the active profile's saved filter, the user has unsaved
 * changes and the UI surfaces a "Save / Reset" affordance.
 */

import { type ConversationFilters } from "@nexus/types"
import { DbEnum } from "@nexus/types/core"
import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

export interface ConversationsState {
  /** Active profile ID. `null` means "no profile selected yet" (pre-hydration). */
  activeProfileId: string | null

  /**
   * Partial overrides on top of the active profile's saved filters. `null`
   * when the user hasn't tweaked anything. Cleared when the active profile
   * changes — overrides do not survive a profile switch.
   */
  filterOverrides: Partial<ConversationFilters> | null

  /**
   * Ad-hoc sort override on top of the active profile's saved `sortBy`.
   * Sort lives outside `ConversationFilters` (it's an ordering, not a
   * predicate), so it has its own override slot rather than smuggling
   * through `filterOverrides`. Cleared on profile switch like the
   * filter overrides.
   */
  sortOverride: DbEnum.ConversationSort | null

  /** Selected thread within the active profile. URL-driven. */
  selectedThreadId: string | null

  /**
   * Right-side details pane collapsed flag (chapter 11 pane chrome).
   * Local UI preference; the conversations module persists it through
   * `useDetailsPanePersistence` so the choice survives reloads. Default
   * `false` so first-time visitors see the pane open (parity with the
   * legacy `ControlSidebar` default behaviour).
   */
  detailsPaneCollapsed: boolean
}

const initialState: ConversationsState = {
  activeProfileId: null,
  filterOverrides: null,
  sortOverride: null,
  selectedThreadId: null,
  detailsPaneCollapsed: false
}

const conversationsSlice = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    /**
     * Switch the active profile. Clears ad-hoc overrides AND the selected
     * thread — a thread that exists in profile A may not match profile B's
     * filters, so the right pane should always reset on profile switch.
     */
    setActiveProfile(state, action: PayloadAction<string | null>) {
      state.activeProfileId = action.payload
      state.filterOverrides = null
      state.sortOverride = null
      state.selectedThreadId = null
    },

    /**
     * Layer a partial filter patch on top of existing overrides. Each axis is
     * replaced wholesale (no deep merge) — `assignees: []` clears the override
     * for that axis, it does not mean "drop the override".
     */
    patchFilterOverrides(state, action: PayloadAction<Partial<ConversationFilters>>) {
      state.filterOverrides = {
        ...(state.filterOverrides ?? {}),
        ...action.payload
      }
    },

    /** Drop all ad-hoc overrides and revert to the active profile's saved filter. */
    clearFilterOverrides(state) {
      state.filterOverrides = null
    },

    /**
     * Override the sort order independently of the saved profile. `null`
     * clears the override and falls back to the profile's saved `sortBy`.
     */
    setSortOverride(state, action: PayloadAction<DbEnum.ConversationSort | null>) {
      state.sortOverride = action.payload
    },

    /**
     * Atomic profile + thread set. Used for URL hydration (deep links)
     * and any other path that needs both slots to land in a single
     * dispatch — avoids the two-render flicker `setActiveProfile`
     * followed by `setSelectedThread` would produce.
     *
     * Clears `filterOverrides` and `sortOverride` ONLY when the profile
     * changes — switching profile invalidates ad-hoc tweaks, but
     * changing only the thread inside the same profile must not.
     */
    setSelection(
      state,
      action: PayloadAction<{ profileId: string | null; threadId: string | null }>
    ) {
      const profileChanged = state.activeProfileId !== action.payload.profileId
      state.activeProfileId = action.payload.profileId
      state.selectedThreadId = action.payload.threadId
      if (profileChanged) {
        state.filterOverrides = null
        state.sortOverride = null
      }
    },

    /** Select a thread within the active profile. */
    setSelectedThread(state, action: PayloadAction<string | null>) {
      state.selectedThreadId = action.payload
    },

    /** Toggle the right-side details pane (UI pref). */
    toggleDetailsPane(state) {
      state.detailsPaneCollapsed = !state.detailsPaneCollapsed
    },

    /** Explicitly set the details-pane collapsed flag (used by the persist hook on hydration). */
    setDetailsPaneCollapsed(state, action: PayloadAction<boolean>) {
      state.detailsPaneCollapsed = action.payload
    }
  }
})

export const {
  setActiveProfile,
  patchFilterOverrides,
  clearFilterOverrides,
  setSortOverride,
  setSelection,
  setSelectedThread,
  toggleDetailsPane,
  setDetailsPaneCollapsed
} = conversationsSlice.actions

export const conversationsReducer = conversationsSlice.reducer
